import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';

import type { BoardDetails } from '../../interfaces/BoardDetails';
import { formatBoardTimerSeconds, getBoardTimerState } from '../../utils/boardTimer';

type BoardTimerProps = {
  boardDetails: BoardDetails;
  onStop: () => Promise<void>;
  onResume: () => Promise<void>;
  onReset: () => Promise<void>;
  onComplete: () => Promise<void>;
};

const BoardTimer = ({
  boardDetails,
  onStop,
  onResume,
  onReset,
  onComplete,
}: BoardTimerProps) => {
  const [now, setNow] = useState(() => Date.now());
  const [isUpdating, setIsUpdating] = useState(false);
  const completedTimerKeyRef = useRef<string | null>(null);
  const timerState = useMemo(() => getBoardTimerState(boardDetails, now), [boardDetails, now]);

  useEffect(() => {
    if (!timerState.isRunning) {
      return;
    }

    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [timerState.isRunning]);

  useEffect(() => {
    setNow(Date.now());
  }, [
    boardDetails.timerStartedAt,
    boardDetails.timerStoppedAt,
    boardDetails.timerDurationSeconds,
    boardDetails.timerStatus,
  ]);

  useEffect(() => {
    const timerKey = [
      boardDetails._id,
      boardDetails.timerStartedAt ?? '',
      boardDetails.timerDurationSeconds ?? '',
    ].join(':');

    if (
      !timerState.isExpired ||
      boardDetails.timerCompletionActionsAppliedAt ||
      completedTimerKeyRef.current === timerKey
    ) {
      return;
    }

    completedTimerKeyRef.current = timerKey;
    void onComplete();
  }, [
    boardDetails._id,
    boardDetails.timerCompletionActionsAppliedAt,
    boardDetails.timerDurationSeconds,
    boardDetails.timerStartedAt,
    onComplete,
    timerState.isExpired,
  ]);

  if (!timerState.hasTimer) {
    return null;
  }

  const timerLabel = formatBoardTimerSeconds(timerState.remainingSeconds);
  const timerColor = timerState.isExpired ? '#c13d6c' : timerState.isStopped ? '#5f6368' : '#111';

  const runTimerAction = async (action: () => Promise<void>) => {
    if (isUpdating) {
      return;
    }

    setIsUpdating(true);

    try {
      await action();
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box
      sx={{
        color: timerColor,
        backgroundColor: '#fff',
        display: 'inline-flex',
        flexShrink: 0,
        alignItems: 'center',
        px: 1.5,
        height: 52,
        minWidth: boardDetails.isOwner ? 160 : 135,
        border: '1px solid #d6d6d6',
        borderRadius: 3,
        boxShadow:
          '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent={boardDetails.isOwner ? 'space-between' : 'center'}
        sx={{ flexGrow: 1 }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent={boardDetails.isOwner ? 'flex-start' : 'center'}
          sx={{ flexGrow: 1 }}
        >
          <TimerOutlinedIcon fontSize="small" />
          <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {timerLabel}
          </Box>
        </Stack>
        {boardDetails.isOwner && (
          <Stack direction="row" spacing={0.25} alignItems="center">
            {timerState.isStopped ? (
              <Tooltip title="Resume timer">
                <span>
                  <IconButton
                    aria-label="Resume timer"
                    size="small"
                    disabled={isUpdating || timerState.remainingSeconds === 0}
                    onClick={() => void runTimerAction(onResume)}
                    sx={{ color: timerColor, p: 0.25 }}
                  >
                    <PlayCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Pause timer">
                <span>
                  <IconButton
                    aria-label="Pause timer"
                    size="small"
                    disabled={isUpdating || !timerState.isRunning}
                    onClick={() => void runTimerAction(onStop)}
                    sx={{ color: timerColor, p: 0.25 }}
                  >
                    <PauseCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Reset timer">
              <span>
                <IconButton
                  aria-label="Reset timer"
                  size="small"
                  disabled={isUpdating}
                  onClick={() => void runTimerAction(onReset)}
                  sx={{ color: timerColor, p: 0.25 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default BoardTimer;
